import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuditLogServiceV1ListAuditLogsQuery } from "_services/modules/audit-log/audit-log";
import type { AuditLogItem } from "_interfaces/audit-log.interfaces";

/**
 * The ONLY stateful piece of the audit-history system.
 *
 * Owns the network boundary: query gating, incremental pagination
 * (infinite scroll), and server-side date filtering. Knows nothing
 * about diffing, grouping, or presentation.
 *
 * Pagination model: pages accumulate client-side (id-deduped). The
 * accumulator resets whenever the base args change (entity, dates) or
 * the sidebar reopens — every view starts from page 1 of a fresh fetch.
 *
 * The state it returns is deliberately explicit — callers can always
 * tell what is loading, how much data exists, and whether more pages
 * are available. No silent truncation.
 */

export interface AuditLogFeedState {
  /** True while nothing is on screen and the first page is loading. */
  isLoading: boolean;
  /** True during any fetch, including load-more. */
  isFetching: boolean;
  /** True when a follow-up page is loading (rows already on screen). */
  isLoadingMore: boolean;
  /** Server-reported total matching the current filter. */
  total: number;
  /** True when the server says another page exists. */
  hasMore: boolean;
  error: unknown;
}

export interface AuditLogFeed {
  logs: AuditLogItem[];
  state: AuditLogFeedState;
  fromDateValue: string;
  toDateValue: string;
  setDateRange: (from: string, to: string) => void;
  /** Fetch the next page and append. No-op while fetching or exhausted. */
  loadMore: () => void;
  /** Drop accumulated pages and start over from page 1. */
  reset: () => void;
}

interface UseAuditLogFeedOptions {
  entityType: string;
  entityId: string;
  /** Fetch only while the history sidebar is open. */
  isOpen: boolean;
  /** Rows per page. Default 10. */
  pageSize?: number;
  /** Extra off-switch (e.g. injected-logs mode). ANDed with the defaults. */
  enabled?: boolean;
}

// Server date bounds, matching the contract fleet already uses.
const toStartBound = (date: string) => (date ? `${date}T00:00:00Z` : undefined);
const toEndBound = (date: string) => (date ? `${date}T23:59:59Z` : undefined);

export const useAuditLogFeed = ({
  entityType,
  entityId,
  isOpen,
  pageSize = 10,
  enabled = true,
}: UseAuditLogFeedOptions): AuditLogFeed => {
  const [page, setPage] = useState(1);
  const [fromDateValue, setFromDateValue] = useState("");
  const [toDateValue, setToDateValue] = useState("");
  const [mergedLogs, setMergedLogs] = useState<AuditLogItem[]>([]);

  const canFetch = Boolean(entityType && entityId && isOpen) && enabled;

  // Dev tripwire: an open sidebar with incomplete args fails silently
  // (skip = no fetch = clean-looking empty history). Announce it.
  if (process.env.NODE_ENV === "development" && isOpen && (!entityType || !entityId)) {
    console.warn(
      "[useAuditLogFeed] sidebar is open without entityType/entityId — fetch skipped",
      { entityType, entityId },
    );
  }

  // Base args (entity, dates) or open-state change → fresh start.
  const argKey = `${entityType}|${entityId}|${fromDateValue}|${toDateValue}`;
  const reset = useCallback(() => {
    setPage(1);
    setMergedLogs([]);
  }, []);
  useEffect(() => {
    reset();
  }, [argKey, isOpen, reset]);

  const { currentData, isLoading, isFetching, error } =
    useAuditLogServiceV1ListAuditLogsQuery(
      {
        entityType: entityType || "",
        entityId: entityId || "",
        page,
        size: pageSize,
        startDate: toStartBound(fromDateValue),
        endDate: toEndBound(toDateValue || fromDateValue),
      },
      { skip: !canFetch },
    );

  // Merge the page that matches the CURRENT args into the accumulator.
  // currentData (not data) guarantees we never merge a page belonging
  // to args that were already superseded. Id-deduped, so a re-run of
  // this effect (StrictMode double-invoke) is a no-op.
  const incoming = currentData?.audit_logs as AuditLogItem[] | undefined;
  useEffect(() => {
    if (!incoming?.length) return;
    setMergedLogs((prev) => {
      const seen = new Set(prev.map((log) => log.id));
      const fresh = incoming.filter((log) => !seen.has(log.id));
      return fresh.length > 0 ? [...prev, ...fresh] : prev;
    });
  }, [incoming]);

  const setDateRange = useCallback(
    (from: string, to: string) => {
      setFromDateValue(from);
      setToDateValue(to);
    },
    [],
  );

  const metadata = currentData?.metadata;
  const total = metadata?.total ?? 0;
  const hasMore = metadata ? Boolean(metadata.have_next_page) : false;

  const loadMore = useCallback(() => {
    if (!hasMore || isFetching) return;
    setPage((current) => current + 1);
  }, [hasMore, isFetching]);

  const state: AuditLogFeedState = {
    isLoading: canFetch ? isFetching && mergedLogs.length === 0 : false,
    isFetching: canFetch ? isFetching : false,
    isLoadingMore: canFetch ? isFetching && mergedLogs.length > 0 : false,
    total,
    hasMore,
    error,
  };

  const logs = mergedLogs;

  return {
    logs,
    state,
    fromDateValue,
    toDateValue,
    setDateRange,
    loadMore,
    reset,
  };
};
